import { useEffect, useMemo, useRef, useState } from "react";
import { Crown, Check, CreditCard } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, apiClient } from "../App";
import { toast } from "sonner";

const PRO_MONTHLY_EUR = 19;
const PRO_YEARLY_EUR = 190;

export default function BillingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, checkAuth } = useAuth();
  const [cycle, setCycle] = useState("monthly");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const checkoutSyncRef = useRef(null);

  const checkoutParam = searchParams.get("checkout");
  const sessionIdParam = searchParams.get("session_id");

  useEffect(() => {
    if (checkoutParam !== "success" || !sessionIdParam) return undefined;
    if (checkoutSyncRef.current === sessionIdParam) return undefined;
    checkoutSyncRef.current = sessionIdParam;

    let cancelled = false;
    (async () => {
      try {
        await apiClient.post("/billing/complete-checkout", { session_id: sessionIdParam });
        if (!cancelled) {
          toast.success("Pro plan activated.");
          await checkAuth();
        }
      } catch (error) {
        const detail = error?.response?.data?.detail;
        if (!cancelled) {
          toast.error(detail || "Could not confirm your subscription yet. Try refreshing the page.");
        }
      } finally {
        if (!cancelled) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete("checkout");
            next.delete("session_id");
            return next;
          }, { replace: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [checkoutParam, sessionIdParam, setSearchParams, checkAuth]);

  const normalizedPlan = (user?.plan || "free").toLowerCase();
  const isPro = normalizedPlan === "pro" || normalizedPlan === "full_access";
  const currentPrice = cycle === "yearly" ? PRO_YEARLY_EUR : PRO_MONTHLY_EUR;
  const currentLabel = cycle === "yearly" ? "per year" : "per month";

  useEffect(() => {
    if (!isPro) {
      setBillingStatus(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get("/billing/status");
        if (!cancelled) setBillingStatus(data || null);
      } catch (_error) {
        if (!cancelled) setBillingStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPro]);

  const cancelEndsAt = (() => {
    if (!billingStatus?.cancel_at_period_end) return null;
    const ts = billingStatus.cancel_at || billingStatus.current_period_end;
    if (!ts) return null;
    const d = new Date(Number(ts) * 1000);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  })();

  const freeFeatures = useMemo(
    () => [
      "Explore the opportunities radar — browse, filters, and rich opportunity pages (titles, verdict, sidebar)",
      "Top‑10 leaderboard snapshot — full visibility",
      "Rich opportunity detail — verified metrics where available, peer benchmarks, signal read, insights, playbook, tech stack cues, and risk notes",
      "SaaS idea checker in the dashboard: 1 validated idea per sliding day window",
      "Guest landing check: try one analyze before signup (cookies apply)",
      "Save opportunities to My Projects — up to 5 on Free (upgrade for unlimited saves)",
    ],
    []
  );

  const proFeatures = useMemo(
    () => [
      "Everything in Free — unlimited saved projects (no cap)",
      "SaaS idea checker: up to 8 validations per sliding 24-hour window (Free: 1/day)",
      "Highest usage limits — same intelligence views as Free, faster iteration on ideas and benchmarks",
      "Full Cofounder access (guided planning; higher monthly token allowances) and premium MVP prompts",
      "Human support prioritized for Pro subscribers when we offer it",
    ],
    []
  );

  const handleUpgrade = async () => {
    if (isPro) {
      toast.success("You are already on Pro.");
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await apiClient.post("/billing/checkout-session", { cycle });
      const checkoutUrl = data?.url;
      if (!checkoutUrl) {
        throw new Error("Missing Stripe checkout URL");
      }
      window.location.assign(checkoutUrl);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || "Could not start Stripe checkout.");
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data } = await apiClient.post("/billing/customer-portal-session");
      const portalUrl = data?.url;
      if (!portalUrl) {
        throw new Error("Missing Stripe portal URL");
      }
      window.location.assign(portalUrl);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || "Could not open billing portal.");
      setPortalLoading(false);
    }
  };

  const handleSyncPlanFromStripe = async () => {
    setSyncLoading(true);
    try {
      const { data } = await apiClient.post("/billing/sync-subscription");
      const plan = (data?.plan || "").toLowerCase();
      if (plan === "pro" || plan === "full_access") {
        toast.success("Your plan is now Pro.");
      } else {
        toast.info("No active subscription found for this email in Stripe.");
      }
      await checkAuth();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || "Could not sync with Stripe.");
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="px-4 py-5 md:p-8 lg:p-12 max-w-5xl">
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Billing
        </h1>
        <p className="text-zinc-400">Choose your plan and billing cycle.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-[#1F1F1F] bg-[#111111] p-5 md:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500 mb-3">Free</p>
          <h2 className="text-2xl font-semibold text-white mb-2">Free Plan</h2>
          <p className="text-zinc-400 text-sm mb-5">Good for discovering opportunities and testing the platform.</p>
          <p className="text-4xl font-bold text-white mb-4">€0</p>
          <ul className="space-y-2 text-sm text-zinc-300">
            {freeFeatures.map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-zinc-500" />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border border-[#7C3AED]/35 bg-gradient-to-br from-[#7C3AED]/10 to-[#111111] p-5 md:p-8">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#C4B5FD]">Pro</p>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#7C3AED]/35 bg-[#7C3AED]/15 px-2 py-0.5 text-[11px] text-[#D6BCFA]">
              <Crown className="w-3.5 h-3.5" />
              Recommended
            </span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">Pro Plan</h2>
          <p className="text-zinc-300 text-sm mb-5">Unlock Cofounder and all core product capabilities.</p>

          <div className="mb-4 relative inline-grid grid-cols-2 rounded-xl border border-[#2A2A2A] bg-black/20 p-1 overflow-hidden">
            <span
              className={`pointer-events-none absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6] shadow-[0_6px_20px_rgba(124,58,237,0.35)] transition-transform duration-300 ease-out ${
                cycle === "yearly" ? "translate-x-[calc(100%+4px)]" : "translate-x-0"
              }`}
            />
            <button
              type="button"
              className={`relative z-10 px-3 py-1.5 text-sm rounded-lg transition-colors duration-300 ${
                cycle === "monthly" ? "text-white" : "text-zinc-400 hover:text-zinc-200"
              }`}
              onClick={() => setCycle("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`relative z-10 px-3 py-1.5 text-sm rounded-lg transition-colors duration-300 ${
                cycle === "yearly" ? "text-white" : "text-zinc-400 hover:text-zinc-200"
              }`}
              onClick={() => setCycle("yearly")}
            >
              Yearly
            </button>
          </div>

          <p className="text-4xl font-bold text-white mb-1">€{currentPrice}</p>
          <p className="text-xs text-zinc-400 mb-4">{currentLabel}{cycle === "yearly" ? " (2 months free)" : ""}</p>

          <ul className="space-y-2 text-sm text-zinc-200 mb-6">
            {proFeatures.map((feature) => (
              <li key={feature} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#C4B5FD]" />
                {feature}
              </li>
            ))}
          </ul>

          <Button className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleUpgrade} data-testid="billing-upgrade-btn" disabled={checkoutLoading}>
            <CreditCard className="w-4 h-4 mr-2" />
            {isPro ? "Current plan: Pro" : checkoutLoading ? "Redirecting to Stripe..." : `Upgrade to Pro (${cycle})`}
          </Button>
          {!isPro ? (
            <button
              type="button"
              className="mt-3 w-full text-center text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-300 disabled:opacity-50"
              onClick={handleSyncPlanFromStripe}
              disabled={syncLoading}
            >
              {syncLoading ? "Syncing with Stripe…" : "Already paid? Sync plan from Stripe"}
            </button>
          ) : null}
          {isPro ? (
            <>
              {cancelEndsAt ? (
                <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                  Your subscription is scheduled to end on <strong>{cancelEndsAt}</strong>. You keep Pro access until that date. You can reactivate anytime from the billing portal.
                </div>
              ) : null}
              <Button
                variant="outline"
                className="mt-3 w-full border-[#2A2A2A] bg-black/20 text-zinc-200 hover:bg-white/10"
                onClick={handleManageSubscription}
                disabled={portalLoading}
                data-testid="billing-manage-subscription-btn"
              >
                {portalLoading ? "Opening billing portal..." : "Manage subscription"}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <Button variant="ghost" className="mt-6 text-zinc-400 hover:text-white hover:bg-white/5" onClick={() => navigate("/dashboard/settings")}>
        Back to Settings
      </Button>
    </div>
  );
}

